import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Scale, Calendar, Award, AlertTriangle, Shield } from 'lucide-react';
import Footer from '../components/Footer';

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Bases Legales - Concurso de Árboles de Navidad</title>
        <meta 
          name="description" 
          content="Bases legales y términos y condiciones del concurso de árboles de Navidad." 
        />
      </Helmet>

      <div className="min-h-screen pt-16">
        <div className="bg-primary text-white py-20">
          <div className="container mx-auto px-4 text-center">
            <Scale className="h-16 w-16 mx-auto mb-6 text-secondary" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Bases Legales</h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Términos y condiciones del concurso
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto space-y-12">
            <section>
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">Organizador</h2>
              </div>
              <div className="prose prose-lg">
                <p>
                  El concurso está organizado por Iznoval Digital Solutions S.L., con CIF B40648214 
                  y domicilio en Avenida Camp de Morvedre 99, Puerto de Sagunto, Valencia.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <Calendar className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">Duración y Ámbito</h2>
              </div>
              <div className="prose prose-lg">
                <ul>
                  <li>Inicio del concurso: 1 de diciembre de 2023</li>
                  <li>Fin de participación: 20 de diciembre de 2023</li>
                  <li>Votación: hasta el 24 de diciembre de 2023</li>
                  <li>Anuncio de ganadores: 25 de diciembre de 2023</li>
                  <li>Ámbito: España peninsular</li>
                </ul>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <Award className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">Premios</h2>
              </div>
              <div className="prose prose-lg">
                <ul>
                  <li>
                    <strong>Primer Premio:</strong>
                    <ul>
                      <li>Lote de Navidad Premium que incluye:</li>
                      <li>Bebidas premium (cava, vinos, ginebra)</li>
                      <li>Charcutería ibérica selecta</li>
                      <li>Conservas gourmet</li>
                      <li>Turrones artesanos</li>
                      <li>Valorado en más de 200€</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Segundo Premio:</strong>
                    <ul>
                      <li>Tarjeta Regalo El Corte Inglés</li>
                      <li>Valor de 75€</li>
                      <li>Válida en todas las tiendas</li>
                      <li>1 año de validez</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Tercer Premio:</strong>
                    <ul>
                      <li>Tarjeta Regalo TodoJuguete</li>
                      <li>Valor de 40€</li>
                      <li>Para compras online</li>
                      <li>Envío gratis incluido</li>
                    </ul>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">Condiciones de Participación</h2>
              </div>
              <div className="prose prose-lg">
                <ul>
                  <li>Ser mayor de 18 años</li>
                  <li>Residir en España peninsular</li>
                  <li>Una única participación por persona</li>
                  <li>La foto debe ser original y actual</li>
                  <li>El árbol debe estar decorado por el participante</li>
                  <li>La imagen debe cumplir los requisitos técnicos especificados</li>
                  <li>No se admiten contenidos ofensivos o inapropiados</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Mecánica del Concurso</h2>
              <div className="prose prose-lg">
                <ol>
                  <li>Subir una foto del árbol de Navidad</li>
                  <li>Completar el formulario de participación</li>
                  <li>La foto será revisada y aprobada por el equipo</li>
                  <li>Una vez aprobada, entrará en la fase de votación</li>
                  <li>Los usuarios podrán votar una vez cada 12 horas</li>
                  <li>Los ganadores serán los que más votos acumulen</li>
                </ol>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Entrega de Premios</h2>
              <div className="prose prose-lg">
                <ul>
                  <li>Los ganadores serán contactados por email</li>
                  <li>Plazo de 48 horas para aceptar el premio</li>
                  <li>Entrega en evento especial el 26 de diciembre</li>
                  <li>Necesario presentar DNI para recoger el premio</li>
                  <li>Los premios no son transferibles ni canjeables por dinero</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Protección de Datos</h2>
              <div className="prose prose-lg">
                <p>
                  Los datos personales serán tratados según nuestra{' '}
                  <a href="/privacidad" className="text-primary hover:text-primary-dark">
                    Política de Privacidad
                  </a>
                  . Al participar, aceptas el tratamiento de tus datos para los fines del concurso.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Modificaciones</h2>
              <div className="prose prose-lg">
                <p>
                  El organizador se reserva el derecho de modificar o cancelar el concurso por 
                  causas justificadas. Cualquier modificación será comunicada a través de la web.
                </p>
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}